/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import {
  defineCreatedEvents,
  defineDeletedEvents,
  defineUpdatedEvents,
  defineSearchedEvents,
  defineCTAEvents,
  defineRanProcessEvents,
  definePageEvents,
  defineExperimentEvents,
  defineModalEvents,
  defineVideoEvents
} from './segmentUtil';

/*
 *
 * Create Object Events:
 *   - Any object that is 'created' should trigger a Created Object event.
 *
 * Event definition:
 *
 *     triggerName         - label used to trigger the event from the code. Part of definition. Not used in Amplitude
 *     action              - Typically a brief description of what is being created. Used to set the 'action' property in Segment/Amplitude. Part of definition. Do not override
 *     objectType          - Type of object that is being created. Used to set objectType property in Segment/Amplitude. Part of definition. Do not override
 *     {property buckets}  - additional property buckets that can be sent with the fired event (optional)
 *
 */

// WA supported properties for Created Object events
// These properties match the allowed schema. Use these properties and property buckets vs adding specific custom properties
// Custom properties should not be used unless something is needed beyond these default properties and property buckets
// Do not override properties that are set in the event definition.
export const createEventProps = {
  actionType: 'set by utility - do not override', // eg 'Created'
  action: 'set by definition - do not override', // eg 'Assistant Created'
  objectType: 'set by definition - do not override', // eg 'assistant'
  object: 'Required. Passed by caller when triggering the event', // Unique identifier for the object being created.
  resultValue: 'Required. Passed by caller when triggering the event', // Return message on the object creation. eg Success
  successFlag: true, // Required. Passed by the caller when triggering the event. Indicates if the create object successful?
  parentPageName: null, // Not Required. Passed by caller when triggering the event. Provides page where event is fired from.
  category: null, //Not Required. Passed by caller when triggering the event. Provides additional details on the created event is needed
  process: null //Not required. Passed by caller when triggering the event. Provides details on what is being updated when applicable
};

export const createdObjectEvents = defineCreatedEvents(
  createEventProps,
  `
  # ----------------------------------------------------------------
  # --------------------- Classic Only -----------------------------
  # ----------------------------------------------------------------
  discovery:                 "Discovery Instance Created"  discovery_instance   {common environment}
  skill:                     "Skill Created"               skill                {common environment}
  snapshot:                  "Snapshot Created"            snapshot             {common environment skill}

  # ----------------------------------------------------------------
  # --------------------- New IA and Classic -----------------------
  # ----------------------------------------------------------------
  # --------------------- Actions ----------------------------------
  action:                    "Action Created"              action               {common assistant environment skill}
  globalVariable:            "Global Variable Created"     global_variable      {common assistant environment skill}

  # --------------------- Dialog -----------------------------------
  dialogNode:                "Dialog Node Created"         dialog_node          {common assistant environment skill}
  entity:                    "Entity Created"              entity               {common assistant environment skill}
  entityValue:               "Entity Value Created"        entity_value         {common assistant environment skill}
  intent:                    "Intent Created"              intent               {common assistant environment skill}
  userExample:               "User Example Created"        user_example         {common assistant environment skill}

  # --------------------- Integrations -----------------------------
  integration:               "Integration Created"         integration          {common assistant environment integration}
  stt:                       "STT Instance Created"        stt_instance         {common assistant environment}
  tts:                       "TTS Instance Created"        tts_instance         {common assistant environment}

  # --------------------- Other ----------------------------------
  assistant:                 "Assistant Created"           assistant            {common environment}
  environment:               "Environment Created"         environment          {common environment assistant}

  # ----------------------------------------------------------------
  # --------------------- New IA Only ------------------------------
  # ----------------------------------------------------------------
  # --------------------- Integrations -----------------------------
  customExtension:           "Custom Extension Created"    custom_extension     {common assistant environment customExtension}
  release:                   "Release Created"             assistant            {common environment skill}
  `
);

/*
 *
 * Update Object Events:
 *   - Any object that is 'updated' should trigger a Update Object event.
 *
 * Event definition:
 *
 *     triggerName         - label used to trigger the event from the code. Part of definition. Not used in Amplitude
 *     action              - Typically a brief description of what is being updated. Used to set the 'action' property in Segment/Amplitude. Part of definition. Do not override
 *     objectType          - Type of object that is being created. Used to set objectType property in Segment/Amplitude. Part of definition. Do not override
 *     {property buckets}  - additional property buckets that can be sent with the fired event (optional)
 *
 */

// WA supported properties for Updated Object events
// These properties match the allowed schema. Use these properties and property buckets vs adding specific custom properties
// Custom properties should not be used unless something is needed beyond these default properties and property buckets
// Do not override properties that are set in the event definition.
export const updateEventProps = {
  actionType: 'set by utility - do not override', // eg 'Updated'
  action: 'set by definition - do not override', // eg 'Assistant Updated'
  objectType: 'set by definition - do not override', // eg 'assistant'
  object: 'Required. Passed by caller when triggering the event', // Unique identifier for the object being updated.
  resultValue: 'Required. Passed by caller when triggering the event', // Return message on the object update. eg Success
  successFlag: true, // Required. Passed by the caller when triggering the event. Indicates if the update object successful?
  parentPageName: null, // Not Required. Passed by caller when triggering the event. Provides page where event is fired from.
  category: null, //Not Required. Passed by caller when triggering the event. Provides further detauls on the page that is being updated
  process: null //Not required. Passed by caller when triggering the event. Provides details on what is being updated when applicable
};

export const updatedObjectEvents = defineUpdatedEvents(
  updateEventProps,
  `
  # ----------------------------------------------------------------
  # --------------------- Classic Only -----------------------------
  # ----------------------------------------------------------------

  # ----------------------------------------------------------------
  # --------------------- New IA and Classic -----------------------
  # ----------------------------------------------------------------
  taskList:        "Task List Updated"           task_list            {common assistant environment}

  # --------------------- Actions ----------------------------------
  action:                   "Action Updated"                           action                {common assistant environment skill action}
  globalVariable:           "Global Variable Updated"                  global_variable       {common assistant environment skill}
  skill:                    "Skill Updated"                            skill                 {common assistant environment skill}
  step:                     "Step Updated"                             step                  {common assistant environment skill action step}
  dynamicSubaction:         "Step Updated with Let Assistant Decide"   step                  {common assistant environment skill action step}

  # --------------------- Dialog -----------------------------------
  dialogNode:      "Dialog Node Updated"         dialog_node          {common assistant environment skill}
  entity:          "Entity Updated"              entity               {common assistant environment skill}
  entityValue:     "Entity Value Updated"        entity_value         {common assistant environment skill}
  intent:          "Intent Updated"              intent               {common assistant environment skill}
  userExample:     "User Example Updated"        user_example         {common assistant environment skill}
  webhooks:        "Webhook Updated"             webhooks             {common assistant environment skill}

  # --------------------- Integrations -----------------------------
  integration:     "Integration Updated"         integration          {common assistant environment integration}

  # --------------------- Other -----------------------------------
  assistant:       "Assistant Updated"           assistant            {common environment assistant}
  environment:     "Environment Updated"         environment          {common environment assistant}
`
);

/*
 *
 * Deleted Object Events:
 *   - Any object that is 'deleted' should trigger a deleted Object event.
 *
 * Event definition:
 *
 *     triggerName         - label used to trigger the event from the code. Part of definition. Not used in Amplitude
 *     action              - Typically a brief description of what is being updated. Used to set the 'action' property in Segment/Amplitude. Part of definition. Do not override
 *     objectType          - Type of object that is being created. Used to set objectType property in Segment/Amplitude. Part of definition. Do not override
 *     {property buckets}  - additional property buckets that can be sent with the fired event (optional)
 *
 */

// WA supported properties for Updated Object events
// These properties match the allowed schema. Use these properties and property buckets vs adding specific custom properties
// Custom properties should not be used unless something is needed beyond these default properties and property buckets
// Do not override properties that are set in the event definition.
export const deletedEventProps = {
  actionType: 'set by utility - do not override', // eg 'Deleted'
  action: 'set by definition - do not override', // eg 'Assistant Deleted'
  objectType: 'set by definition - do not override', // eg 'assistant'
  object: 'Required. Passed by caller when triggering the event', // Unique identifier for the object being deleted.
  resultValue: 'Required. Passed by caller when triggering the event', // Return message on the object delete. eg Success
  successFlag: true, // Required. Passed by the caller when triggering the event. Indicates if the delete object successful?
  parentPageName: null, // Not Required. Passed by caller when triggering the event. Provides page where event is fired from.
  category: null //Not Required. Passed by caller when triggering the event. Provides further detauls on the page that is being updated
  //process: null -> deletedObject event in IBM cloud does not support process like it does for other object events.
};

export const deletedObjectEvents = defineDeletedEvents(
  deletedEventProps,
  `
  # ----------------------------------------------------------------
  # --------------------- Classic Only -----------------------------
  # ----------------------------------------------------------------
  skill:           "Skill Deleted"               skill                {common assistant environment skill}
  snapshot:        "Snapshot Deleted"            snapshot             {common assistant environment skill}

  # ----------------------------------------------------------------
  # --------------------- New IA and Classic -----------------------
  # ----------------------------------------------------------------
  # --------------------- Actions ----------------------------------
  action:          "Action Deleted"              action               {common assistant environment skill action}
  globalVariable:  "Global Variable Deleted"     global_variable      {common assistant environment skill}
  step:            "Step Deleted"                step                 {common assistant environment skill action step}

  # --------------------- Dialog ----------------------------------
  entity:          "Entity Deleted"              entity               {common assistant environment skill}
  entityValue:     "Entity Value Deleted"        entity_value         {common assistant environment skill}
  intent:          "Intent Deleted"              intent               {common assistant environment skill}
  userExample:     "User Example Deleted"        user_example         {common assistant environment skill}

  # --------------------- Integration -----------------------------
  integration:     "Integration Deleted"         integration          {common assistant environment integration}


  # --------------------- Other -----------------------------------
  assistant:       "Assistant Deleted"           assistant            {common assistant}
  environment:     "Environment Deleted"         environment          {common assistant environment}
  webhooks:        "Webhook Deleted"             webhooks             {common assistant environment skill}

  # ----------------------------------------------------------------
  # --------------------- New IA Only ------------------------------
  # ----------------------------------------------------------------
  release:         "Release Deleted"             release              {common assistant environment skill}
`
);

/*
 * Ran Process Events:
 *   - Any user action that triggers some sort of process is considered a Ran Process event. This most often includes
 *     any event that triggers a backend call.
 *
 * Event definition:
 *
 *     triggerName         - label used to trigger the event from the code. Part of definition. Not used in Amplitude
 *     action              - Typically a brief description of what is being run. Used to set the 'action' property in Segment/Amplitude. Part of definition. Do not override
 *     processType         - Additional description of the process. Used to set processType in Segment/Amplitude. Part of definition. Do not override
 *     {property buckets}  - additional property buckets that can be sent with the fired event (optional)
 *
 */

// WA supported properties for Ran Process events
// These properties match the allowed schema. Use these properties and property buckets vs adding specific custom properties
// Custom properties should not be used unless something is needed beyond these default properties and property buckets
// Do not override properties that are set in the event definition.
export const ranProcessEventProps = {
  action: 'set by definition - do not override',
  processType: 'set by defition - do not override',
  successFlag: false, //Required. Passed by caller when triggering the event. Indicates if the process was successful or not. true/false
  resultValue: null, //Not Required. Passed by caller when triggering the event. Provides the return message on the run process. Should be included when possible.
  objectType: null, //Not Required. Passed by caller when triggering the event. Provides the objectType for the object that the process was ran for
  object: null, // Not Required. Passed by caller when triggering the event. Provides the unique identifier for the object that the process was ran for.
  // If no unique identifier is available one should be generated. Eg: assistant GUID + name,
  category: null //Not Required. Passed by caller when triggering the event. Provides additional details on the page being loaded. Eg a specific page process was run
};

export const ranProcessEvents = defineRanProcessEvents(
  ranProcessEventProps,
  `
  # ----------------------------------------------------------------
  # --------------------- Classic Only -----------------------------
  # ----------------------------------------------------------------
  firstAssistant:         "New Assistant Flow Triggered"      new_user                     {common}

  # ----------------------------------------------------------------
  # --------------------- New IA and Classic -----------------------
  # ----------------------------------------------------------------
  # --------------------- Actions ----------------------------------
  disambiguationsSettings:  "Disambiguations Settings Updated"      disambiguations             {common assistant environment skill}
  launchModeSettings:       "Response Mode Settings Updated"        launch_mode                 {common assistant environment skill}
  stepReordered:            "Step Reordered"                        step_reorder                {common assistant environment skill action step}
  tryIt:                    "Try It Used"                           skill_testing               {common assistant environment skill}
  variableSet:              "Variable Set"                          variable_set                {common assistant environment skill action step}
  actionsUploaded:          "Actions Uploaded"                      actions_uploaded            {common assistant environment skill}
  savedResponsesUploaded:   "Saved Responses Uploaded"              savedResponses_uploaded     {common assistant environment skill}

  # --------------------- Other ----------------------------------
  custom:                 "Custom Event"                      custom                       {common}
  assistantMsg:           "UI Assistant Message Sent"         assistant_message            {common}
  userEmailsInvited:      "User Emails Invited to Instance"   user_emails_invited          {common}

  # ----------------------------------------------------------------
  # --------------------- New IA Only ------------------------------
  # ----------------------------------------------------------------
  publish:                 "Publish Event"                     publish                      {common assistant environment skill}
  revert:                  "Revert Event"                      revert                       {common assistant environment skill}
  switchVersion:           "Switch Version Event"              switchVersion                {common assistant environment skill}
  activityLogTableUpdated: "Activity Log Table Updated"        activityLog                  {common assistant environment skill}
  multilingualActivated:   "Multilingual Activated"            multilingual                 {common assistant environment skill}
  assistantDownloaded:     "Assistant Downloaded"              multilingual                 {common assistant environment skill}
  assistantUploaded:       "Assistant Uploaded"                multilingual                 {common assistant environment skill}
`
);

/*
 * CTA Events:
 *   - Call to Action events track any click, selected or toggled user events on a specific control (eg button, link)
 *
 *  NOTE: There are events in the current list that are better suited for other event types (eg Ran Process, Modal Viewed).
 *        These events should be migrated as appropriate
 *
 *  *** It is NOT necessary to track every user click. Before implementing a new segment event think about whether there is
 *  *** any value in tracking the user event you want to add.(eg adding a event for a drop down provides no value until
 *  *** the user action is actually completed. )
 *
 * Event definition:
 *
 *     triggerName         - label used to trigger the event from the code. Part of definition. Not used in Amplitude
 *     action              - Typically a brief description of the CTA. Used to set the 'action' property in Segment/Amplitude. Part of definition. Do not override
 *     actionType          - Type of CTA (clicked, toggled, selected). Used to set actionType in Segment/Amplitude. Part of definition. Do not override
 *     {property buckets}  - additional property buckets that can be sent with the fired event (optional)
 *
 */

// WA supported properties for CTA events
// These properties match the allowed schema. Use these properties and property buckets vs adding specific custom properties
// Custom properties should not be used unless something is needed beyond these default properties and property buckets
// Do not override properties that are set in the event definition.

export const ctaEventProps = {
  CTA: 'set by definition - do not override', //set the same as actions. Required by segment in order to avoid blocking
  action: 'set by definition - do not override', // set the same as the CTA property
  actionType: 'set by definition - do not override', // clicked, selected, toggled
  successFlag: null, //'Not Required. Passed by caller when triggering the event', //Result of CTA (true/false)
  resultValue: null, //'Not Required. Passed by caller when triggering the event', //Result of CTA (enabled/disabled/selected/unselected/toggled/untoggled)
  parentPageName: null, //'Not Required. Passed by caller when triggering the event', // Name of page the CTA was launched form
  category: null, //Not Required. Passed by caller when triggering the event. Provides additional details from where the event is being triggerd. Eg a specific tab within a page
  uiElement: null // eg 'button' or 'tooltip' - ui element that was clicked
};

export const ctaEvents = defineCTAEvents(
  ctaEventProps,
  `
  # ----------------------------------------------------------------
  # --------------------- Classic Only -----------------------------
  # ----------------------------------------------------------------
  # --------------------- Dialog Analytics -------------------------
  autolearnAssistant:             "Select an assistant"                           clicked              {common}
  autolearnDisambiguation:        "Disambiguation link"                           clicked              {common}
  autolearnToggle:                "Toggle autolearn"                              clicked              {common}
  chartEmpty:                     "Learn more link"                               clicked              {common skill}
  closeAutolearnModal:            "Close autolearn confirmation modal"            clicked              {common}
  containmentDemo:                "Watch Containment Demo"                        clicked              {common skill}
  containmentModal:               "Learn more link"                               clicked              {common skill}
  learnMore:                      "Learn More"                                    clicked              {common}
  logsFilterNotContained:         "Not contained"                                 clicked              {common skill}
  logsFilterNotCovered:           "Not covered"                                   clicked              {common skill}
  specificNotContained:           "Specific not contained"                        clicked              {common skill}
  specificNotCovered:             "Specific not covered"                          clicked              {common skill}
  totalNotContained:              "Total not contained"                           clicked              {common skill}
  totalNotCovered:                "Total not covered"                             clicked              {common skill}

  # ----------------------------------------------------------------
  # --------------------- Integrations -----------------------------
  phoneNewViewed:                 "Phone Create Page Viewed"                      clicked              {common assistant}
  whatsAppSelected:               "WhatsApp Selected"                             clicked              {common assistant skill}
  whatsAppHelpLinkClicked:        "WhatsApp Help Link Clicked"                    clicked              {common assistant skill}

  # ----------------------------------------------------------------
  # --------------------- Search -----------------------------------
  searchDemo:                     "Watch Search Demo"                             clicked              {common skill}
  searchAdd:                      "Add Search"                                    clicked              {common skill}
  searchExistingAdd:              "Add existing search"                           clicked              {common skill}
  searchNamed:                    "Search named"                                  clicked              {common skill}
  searchCreateCollection:         "Create Discovery collection"                   clicked              {common skill}

  # ----------------------------------------------------------------
  # --------------------- Other ------------------------------------
  importSkill:                    "Import Skill"                                  clicked              {common skill}
  taskListLinkClicked:            "TaskList Link Clicked"                         clicked              {common assistant}
  bidiSelection:                   "Language preference updated"                  selected             {common}
  tryNewIaSelected:               "Try New IA Selected"                           clicked              {common assistant}
  tryNewIaClicked:                "Try New IA Clicked"                            clicked              {common assistant}

  # ----------------------------------------------------------------
  # --------------------- New IA and Classic -----------------------
  # ----------------------------------------------------------------

  # ----------------------------------------------------------------
  # --------------------- Actions ----------------------------------
  actionsButtonClicked:           "Actions button clicked"                        clicked              {common assistant environment skill}
  newAction:                      "New Action Button"                             clicked              {common assistant environment skill}
  newStep:                        "Add New Step"                                  clicked              {common assistant environment skill action step}
  actionDisambiguation:           "Action disambiguation state selected"          toggled              {common assistant environment skill}
  actionsUploadButton:            "Upload Button for Actions"                     clicked              {common assistant environment skill}
  addTriggerPhrases:              "Add trigger phrases"                           clicked              {common assistant environment skill action step}
  boostWordApplyButton:           "Boost Word Apply button"                       clicked              {common assistant environment skill action custom}
  changeStepType:                 "Change step type"                              clicked              {common assistant environment skill action step}
  closeActionButton:              "Step close button"                             clicked              {common assistant environment skill action step}
  closeSkillSettings:             "Skill settings window closed"                  clicked              {common assistant environment skill}
  copyToAssistantButton:          "Copy to Assistant Button"                      clicked              {common assistant environment skill custom}
  copyToAssistantOpenModalButon:  "Copy to Assistant Modal Open"                  clicked              {common assistant environment skill}
  customerResponseDropdown:       "Define customer response"                      clicked              {common assistant environment skill action step}
  customerResponseSettingsApply:  "Customer response settings apply button"       clicked              {common assistant environment skill action step}
  customerResponseSlotFillApply:  "Customer response slot fill apply button"      clicked              {common assistant environment skill action step}
  editResponse:                   "Edit Response"                                 clicked              {common assistant environment skill action step}
  editValidation:                 "Edit Validation"                               clicked              {common assistant environment skill action step}
  deleteStep:                     "Delete step"                                   clicked              {common assistant environment skill action step}
  duplicateStep:                  "Duplicate step"                                clicked              {common assistant environment skill action step}
  disambiguation:                 "Disambiguation"                                clicked              {common assistant environment skill}
  duplicateAction:                "Duplicate action button from modal"            clicked              {common assistant environment skill action}
  discardEditsButton:             "Discard Edits Button"                          clicked              {common assistant environment skill action step}
  exampleMessage:                 "Example message added"                         clicked              {common assistant environment skill action step}
  actionsFilterButton:            "Action Table Search Filter Button"             clicked              {common assistant environment skill}
  globalVariable:                 "New Global Variable Button"                    clicked              {common assistant environment skill}
  insertVariable:                 "Insert variable"                               clicked              {common assistant environment skill action step}
  openAction:                     "Open Action"                                   clicked              {common assistant environment skill action}
  boostWordOpenModalButton:       "Boost Word open modal button"                  clicked              {common assistant environment skill action}
  openDuplicateActionModal:       "Open Duplicate action modal"                   clicked              {common assistant environment skill}
  previewMessageSent:             "Preview Message Sent"                          clicked              {common assistant environment skill action step}
  previewWindowCleared:           "Preview Window Cleared"                        clicked              {common assistant environment skill action step}
  previewWindowClosed:            "Preview Window Closed"                         clicked              {common assistant environment skill action step}
  previewWindowOpened:            "Preview Window Opened"                         clicked              {common assistant environment skill action step}
  saveButton:                     "Save Button"                                   clicked              {common assistant environment skill action step}
  saveActionResponseMode:         "Save action response mode"                     clicked              {common assistant environment skill action}
  setVariableValues:              "Set variable value"                            clicked              {common assistant environment skill action step}
  stepCondition:                  "With/without conditions"                       clicked              {common assistant environment skill action step}
  stepViewed:                     "View Step"                                     clicked              {common asssitant environment skill action step}
  submitTriggerPhrases:           "Submit trigger phrases"                        clicked              {common asssitant environment skill action step}
  savedResponsesUploadButton:     "Upload Button for Saved Responses"             clicked              {common assistant environment skill}

  # ----------------------------------------------------------------
  # --------------------- Dialog -----------------------------------
  dialogHandlers:                 "Manage Handlers"                               clicked              {common assistant environment skill}
  dialogOptions:                  "Dialog Options"                                toggled              {common assistant environment skill}
  entitiesSort:                   "Sort Entities"                                 selected             {common assistant environment skill}
  entitiesFuzzy:                  "Fuzzy Match"                                   toggled              {common assistant environment skill}
  entitiesSystem:                 "System Entities"                               toggled              {common assistant environment skill}
  intentPipeline:                 "Intent Pipeline Option"                        clicked              {common assistant environment skill}
  intentsSort:                    "Sort Intents"                                  selected             {common assistant environment skill}
  jsonEditor:                     "JSON Editor"                                   clicked              {common assistant environment skill}
  tryIt:                          "Try It Panel"                                  clicked              {common assistant environment skill}

  # ----------------------------------------------------------------
  # --------------------- Integrations -----------------------------
  byosdSelected:                  "BYOSD Selected"                                clicked              {common assistant environment skill}
  phoneTestConnectionClicked:     "Phone Test Connection Clicked"                 clicked              {common assistant environment}
  serviceDeskSelected:            "Service Desk Selected"                         clicked              {common assistant environment skill}
  webChatEmbedLinkCopied:         "Web Chat Embed Link Copied"                    clicked              {common assistant environment}
  webChatBrandingToggle:          "Web chat branding toggled"                     toggled              {common assistant environment}
  webChatHomeScreenToggle:        "Web chat home screen toggled"                  toggled              {common assistant environment}
  webChatSecurityToggle:          "Web chat security toggled"                     toggled              {common assistant environment}
  webChatSecurityEncryption:      "Web chat security encryption generated"        clicked              {common assistant environment}
  webChatSuggestionsToggle:       "Web chat suggestions toggled"                  toggled              {common assistant environment}
  whatsAppWebhookCopied:          "WhatsApp Webhook Copied"                       clicked              {common assistant environment skill}
  whatsAppEmbedLinkCopied:        "WhatsApp Embed Link Copied"                    clicked              {common assistant environment skill}
  whatsAppSynced:                 "WhatsApp Integration Synced"                   clicked              {common assistant environment skill}
  webChatSettingsTab:             "Web Chat Settings Tab"                         clicked              {common assistant environment}


  # ----------------------------------------------------------------
  # --------------------- Search -----------------------------------
  searchMessageTabSuccess:        "Search Message Success"                        clicked              {common assistant environment skill action step}
  searchMessageTabNoResults:      "Search Message No Results"                     clicked              {common assistant environment skill action step}
  searchMessageTabError:          "Search Message Error"                          clicked              {common assistant environment skill action step}
  searchFieldsTitle:              "Search Field Title"                            selected             {common assistant environment skill action step}
  searchFieldsBody:               "Search List Body"                              selected             {common assistant environment skill action step}
  searchFieldsUrl:                "Search List URL"                               selected             {common assistant environment skill action step}
w
  # ----------------------------------------------------------------
  # --------------------- Analytics --------------------------------
  chartDatapoint:                 "Chart datapoint"                               clicked              {common skill}

  # ----------------------------------------------------------------
  # --------------------- Other ------------------------------------
  bookConsultation:               "Book A Consultation"                           clicked              {common}
  featureCompare:                 "View Feature Comparison"                       clicked              {common}
  assistantChat:                  "UI Assistant Chat"                             clicked              {common}
  manageUsersLink:                "IAM Manage Users Link Clicked"                 clicked              {common}
  upgrade:                        "Upgrade"                                       clicked              {common}
  upgradeModal:                   "Upgrade Modal"                                 clicked              {common}
  upgradePlan:                    "Upgrade Plan"                                  clicked              {common}
  webhooks:                       "Opened webhook"                                clicked              {common assistant environment}
  whatsNewItem:                   "What's new item Clicked"                       clicked              {common assistant}

  # ----------------------------------------------------------------
  # --------------------- New IA Only ------------------------------
  # ----------------------------------------------------------------

  wizardClicked:                  "Wizard newia 2.0"                              clicked              {common}

  # ----------------------------------------------------------------
  # --------------------- Actions ----------------------------------
  actionsBannerLinkClicked:       "Actions Banner Link Clicked"                   clicked              {common assistant environment skill action}
  saveExtension:                  "Save extension step resolver"                  clicked              {common assistant environment skill action step}
  startFromScratchClicked:        "Start From Scratch Tile Clicked"               clicked              {common assistant environment skill action}
  templateFilterChecked:          "Template Catalog Filter Checked"               selected             {common assistant environment skill action}
  templateTileClicked:            "Template Tile Clicked"                         clicked              {common assistant environment skill action}
  templateBackClicked:            "Template Back To Catalog Clicked"              clicked              {common assistant environment skill action}
  templateSelectClicked:          "Template Select This Template Clicked"         clicked              {common assistant environment skill action}
  templateBucketClicked:          "Template Bucket Clicked"                       clicked              {common assistant environment skill action}
  addTemplatesClicked:            "Add Templates Button Clicked"                  clicked              {common assistant environment skill action}
  goToActionsPageClicked:         "Go To The Actions Page Button Clicked"         clicked              {common assistant environment skill action}

  # ----------------------------------------------------------------
  # --------------------- Connect ----------------------------------
  homeTaskClicked:                "Home Task Item Clicked"                        clicked              {common assistant environment skill}
  homeBannerLinkClicked:          "Home Banner Link Clicked"                      clicked              {common assistant skill}
  classicBannerLinkClicked:       "Classic Banner Link Clicked"                   clicked              {common assistant skill}

  # ----------------------------------------------------------------
  # --------------------- Publish ----------------------------------
  publishOpenPublish:             "Publish Button Clicked"                        clicked              {common assistant skill environment}
  publishOpenRevert:              "Revert Button Clicked"                         clicked              {common assistant skill environment}
  publishOpenSwitchVersion:       "Switch Version Clicked"                        clicked              {common assistant skill environment}
  publishClosePublish:            "Publish Cancel Clicked"                        clicked              {common assistant skill environment}
  publishShowOlderVersions:       "Show Older Versions Clicked"                   clicked              {common assistant skill environment}
  publishDeleteVersion:           "Delete Version Clicked"                        clicked              {common assistant skill environment}
  publishExportVersion:           "Export Version Clicked"                        clicked              {common assistant skill environment}

  # ----------------------------------------------------------------
  # --------------------- Environments ----------------------------------
  addEnvironmentModalClose:       "Add environment Cancel Clicked"                clicked              {common assistant skill environment}
  deleteEnvironmentModalClose:    "Delete environment Cancel Clicked"             clicked              {common assistant skill environment}
  switchEnvironment:              "Switch environment tab Clicked"                clicked              {common assistant skill environment}
  addEnvironmentClicked:          "Add environment button Clicked"                clicked              {common assistant skill environment}
  sharePreviewLink:               "Share environment preview link Clicked"        clicked              {common assistant skill environment}

  # ----------------------------------------------------------------
  # --------------------- Connect ----------------------------------
  connectOpenEnvSettings:         "Connect Open Environment Settings"             clicked              {common assistant environment}
  connectSwitchVersionModal:      "Connect Switch Version Modal"                  clicked              {common assistant environment}
  connectClickPublishContent:     "Connect Click Publish Content"                 clicked              {common assistant environment skill action step}
  connectClickBrowseCatalog:      "Connect Click Browse Catalog"                  clicked              {common assistant environment}
  connectClickEditContent:        "Connect Click Edit Content"                    clicked              {common assistant environment}
  integrationTileClick:           "Integration Tile Clicked"                      clicked              {common assistant environment}

  # ----------------------------------------------------------------
  # --------------------- Preview ----------------------------------
  previewBackgroundEdited:        "Preview Background Edited"                     clicked              {common assistant environment integration}
  previewPreviewLinkCopied:       "Preview Link Copied"                           clicked              {common assistant environment}
  previewWebChatClicked:          "Preview Open Draft Webchat"                    clicked              {common assistant environment integration}

  # ----------------------------------------------------------------
  # --------------------- Activity Log ----------------------------------
  activityLogDetailLinkClicked:   "Activity Log Link Clicked"                     clicked              {common assistant}
  exportActivityLogClicked:       "Export Activity Log"                           clicked              {common assistant}

  # ----------------------------------------------------------------
  # --------------------- Integrations -----------------------------
  customExtensionsOpenCreate:     "Custom Extension Create Clicked"               clicked              {common assistant environment customExtension}
  customExtensionsFinishClicked:  "Custom Extension Finish Clicked"               clicked              {common assistant environment customExtension }
  integrationsOpenDelete:         "Integration Delete Clicked"                    clicked              {common assistant environment}
  integrationsOpenEdit:           "Integration Edit Clicked"                      clicked              {common assistant environment integration}
  integrationsOpenCreate:         "Integration Create Clicked"                    clicked              {common assistant environment}
  phoneIntegrationExited:         "Phone Integration Exited"                      clicked              {common assistant environment}

  # ----------------------------------------------------------------
  # --------------------- Search -----------------------------------
  searchCreateDiscovery:          "Create Discovery instance"                     clicked              {common assistant environment skill}
  searchOpenInDiscovery:          "Open Project or collection In Discovery"       clicked              {common assistant skill}
  searchCreateProject:            "Create Discovery project"                      clicked              {common assistant skill}
  searchNextConfigScreen:         "Next to search config screen"                  clicked              {common assistant environment skill}
  searchCreate:                   "Search create"                                 clicked              {common assistant environment skill}
  searchUpdate:                   "Search update"                                 clicked              {common assistant environment skill}
  searchWebchatMessage:           "Search config page web chat"                   clicked              {common assistant skill}
  searchShowTopNResults:          "Show top results"                              clicked              {common assistant skill}
  searchDisplayShowMoreResults:   "View upto ten results"                         selected             {common assistant skill}
  searchRefinedResults:           "Refine Search Results"                         toggled              {common assistant skill}
  searchHighlightAnswers:         "Highlight answers"                             toggled              {common assistant skill}

  # ----------------------------------------------------------------
  # --------------------- Analytics --------------------------------
  analyticsRangeSelected:         "Analytics range selected"                      clicked              {common environment assistant}
  analyticsDateSelected:          "Analytics date selected"                       clicked              {common environment assistant}
  analyticsRefreshButton:         "Analytics refresh clicked"                     clicked              {common environment assistant}
  analyticsConvoPanelLabel:       "Analytics conversation panel label clicked"    clicked              {common environment assistant}
  analyticsConvoPanelClose:       "Analytics conversation panel closed"           clicked              {common environment assistant}
  analyticsLearnMore:             "Analytics learn more link clicked"             clicked              {common environment assistant}
  analyticsTabChanged:            "Analytics tab clicked"                         clicked              {common environment assistant}
  analyticsIntervalChanged:       "Analytics interval clicked"                    clicked              {common environment assistant}
  analyticsChartChanged:          "Analytics chart type clicked"                  clicked              {common environment assistant}
  analyticsButtonClicked:         "Analytics button clicked"                      clicked              {common environment assistant}
  analyticsLinkClicked:           "Analytics link clicked"                        clicked              {common environment assistant}
  analyticsTableHeaderClicked:    "Analytics table header clicked"                clicked              {common environment assistant}
  analyticsNoAction:              "Create new action button clicked"              clicked              {common environment assistant}
  analyticsNotPublished:          "Publish assistant button clicked"              clicked              {common environment assistant}
  analyticsTablePagination:       "Analytics table pagination"                    clicked              {common environment assistant}
  analyticsDropdownSelected:      "Analytics dropdown selected"                   clicked              {common environment assistant}
  analyticsSearchFilterApplied:   "Analytics search filter applied"               clicked              {common environment assistant}
  analyticsRemoveFilter:          "Analytics remove filter"                       clicked              {common environment assistant}
  analyticsAdditionalFilters:     "Analytics additional filters clicked"          clicked              {common environment assistant}
  analyticsDownloadClicked:       "Analytics download clicked"                    clicked              {common environment assistant}
  analyticsClosePanelClicked:     "Analytics close panel clicked"                 clicked              {common environment assistant}

  # ----------------------------------------------------------------
  # --------------------- Settings ---------------------------------
  settingsOpenCancelModal:             "Settings Open Cancel Confirmation Modal"       clicked              {common assistant}
  settingsSaveAssistant:               "Settings Save Assistant Clicked"               clicked              {common assistant}
  settingsOpenDeleteModal:             "Settings Open Delete Assistant Modal"          clicked              {common assistant}
  settingsDeleteAssistant:             "Settings Delete Assistant Clicked"             clicked              {common assistant}
  settingsOpenDialogConfirmationModal: "Settings Open Dialog Confirmation Modal"       clicked              {common assistant}
  settingsEnableDialog:                "Settings Enable Dialog Clicked"                clicked              {common assistant}
  settingsDisableDialog:               "Settings Disable Dialog Clicked"               clicked              {common assistant}

  # ----------------------------------------------------------------
  # --------------------- Other ------------------------------------
  createOnboardingAssistant:      "Create Onboarding Assistant Clicked"           clicked              {common}
  apiDetails:                     "API Details copied"                            clicked              {common assistant environment}
  assistantChat:                  "UI Assistant Chat"                             clicked              {common}
  createAssistantClicked:         "Create Assistant Clicked"                      clicked              {common assistant}
  restoreClassicIaSelected:       "Restore Classic IA Selected"                   clicked              {common assistant}
  restoreClassicIaClicked:        "Restore Classic IA Clicked"                    clicked              {common assistant}
  leftNavItemClicked:             "Left Nav Item Clicked"                         clicked              {common assistant}
  headerAccountItemClicked:       "Header Account Item Clicked"                   clicked              {common}
  headerManageUsers:              "Header Manage Users Viewed"                    clicked              {common}
  headerLaunchDocs:               "Header Access Control Docs Viewed"             clicked              {common}
  switchInstanceClicked:          "Switch Service Instance Clicked"               clicked              {common}
  onboardingCustomerResponse:     "Onboarding response dropdown clicked"          clicked              {common assistant environment}
  generativeAIToggleClicked:      "Base LLM Button Clicked"                       clicked              {common assistant environment}
`
);

/*
 * Page Events:
 *   - Any page event (note, modal dialogs should trigger a modalEvent)
 *   - In Amplitude shows as Loaded A Page
 *
 * Event definition:
 *
 *     triggerName         - label used to trigger the event from the code. Not used in Amplitude
 *     name                - Name of the modal event usually descibing the window. Used to set the 'name' property in Segment/Amplitude. Do not override
 *     {property buckets}  - additional property buckets that can be sent with the fired event (optional)
 *
 */

// WA Supported properties for page events
// These properties match the allowed schema. Use these properties and property buckets vs adding specific custom properties
// Custom properties should not be used unless something is needed beyond these default properties and property buckets
// Do not override properties that are set in the event definition.
export const pageEventProps = {
  name: 'set by definition - do not override',
  category: null, //Not Required. Passed by caller when triggering the event. Provides additinal details on the page being loaded. Eg a specific tab on a page
  subCategory: null //Not Required. Passed by caller when triggering the event. Provides additinal details on the page being loaded. eg a specific control on page
};

export const pageEvents = definePageEvents(
  pageEventProps,
  `
  # ----------------------------------------------------------------
  # --------------------- Classic Only -----------------------------
  # ----------------------------------------------------------------
  assistants:         "Assistants - List"                          {common}
  analytics:          "Analytics"                                  {common skill}
  integrationAdd:     "Integration - Add"                          {common integration}
  oauth:              "oAuth"                                      {common}
  searchSkillConfig:  "Search Skill Configuration"                 {common skill}
  skills:             "Skills - List"                              {common}
  versions:           "Versions - List"                            {common skill}

  # ----------------------------------------------------------------
  # --------------------- New IA and Classic -----------------------
  # ----------------------------------------------------------------

  # --------------------- Dialog -----------------------------------
  catalog:            "Content Catalog - List"                     {common assistant environment skill}
  dialogNodes:        "Dialog Node - List"                         {common assistant environment skill}
  dialogNodeEdit:     "Dialog Node - Edit"                         {common assistant environment skill}
  dialogNodeClose:    "Closed Dialog Node"                         {common assistant environment skill}
  dialogNodeOpened:   "Created Dialog Node Opened"                 {common assistant environment skill}
  entities:           "Entities - List"                            {common assistant environment skill}
  entityCreate:       "Entities - New"                             {common assistant environment skill}
  entityEdit:         "Entity - Edit"                              {common assistant environment skill}
  intents:            "Intents - List"                             {common assistant environment skill}
  intentCreate:       "Intents - New"                              {common assistant environment skill}
  intentEdit:         "Intent - Edit"                              {common assistant environment skill}
  options:            "Dialog Skill Options"                       {common assistant environment skill}
  intentDetection:    "Intent Detection"                           {common assistant environment skill}

  # --------------------- Actions ----------------------------------
  customAction:       "Custom Action"                              {common assistant environment skill action}
  customActionTable:  "Custom Actions Table"                       {common assistant environment skill action}
  defaultAction:      "Default Action"                             {common asssitant environment skill action}
  defaultActionTable: "Default Actions Table"                      {common assistant environment skill action}
  globalVariables:    "Global Variables Table"                     {common assistant environment skill action}
  skillSettings:      "Skill Settings"                             {common assistant environment skill action}

  # --------------------- Integrations -----------------------------

  # --------------------- Analytics --------------------------------

  # --------------------- Other -----------------------------------
  login:              "Login"                                      {common}
  trialTimer:         "Trial Plan Time Limit Reached"              {common}
  planUsage:          "Plan Usage Limit Reached"                   {}
  instances:          "Instances"                                  {commom}

  # ----------------------------------------------------------------
  # --------------------- New IA Only ------------------------------
  # ----------------------------------------------------------------
  onboarding:         "Onboarding"                                 {common}
  home:               "Home"                                       {common assistant environemnt skill}
  taskList:           "Home Task List"                             {common assistant environment skill}
  connect:            "Connect"                                    {common assistant environment skill}
  environments:       "Environments"                               {common assistant environment skill}
  preview:            "Preview"                                    {common assistant environment skill}
  publish:            "Publish"                                    {common assistant environment skill}
  settings:           "Settings"                                   {common assistant environment}
  internalError:      "Internal Error"                             {common assistant}
  wizard:             "Wizard newia 2.0"                           {common}
  activityLog:        "Activity log"                               {common assistant environment skill}

  # --------------------- Actions -------------------------------
  selectBuildType:    "Select Action Build Type Tearsheet"         {common assistant environment skill action}
  templateCatalog:    "Template Actions Catalog Tearsheet"         {common assistant environment skill action}

  # --------------------- Analytics -------------------------------
  analyticsMessages:  "Analytics Conversations"                    {common assistant environment}
  analyticsOverview:  "Analytics Overview"                         {common assistant environment}
  analyticsRecog:     "Analytics Actions Recognized"               {common assistant environment}
  analyticsUnrecog:   "Analytics Actions Unrecognized"             {common assistant environment}

  # --------------------- Integrations----------------------------
  integrations:       "Integrations"                               {common assistant environment}
  integrationEdit:    "Integration Edit"                           {common assistant environment integration}

  # --------------------- Search ---------------------------------
  searchCreate:       "Create Search Integration"                  {common assistant environment}
  searchEdit:         "Edit Search Integration"                    {common assistant environment}
`
);

/*
 * Modal Events
 * - Any modal dialog
 * - in Amplitude shows as Viewed Modal Events
 *
 * Event definition:
 *
 *     triggerName         - label used to trigger the event from the code. Not used in Amplitude
 *     name                - Name of the modal event usually describing the window. Used to set the 'name' property in Segment/Amplitude. Do not override
 *     {property buckets}  - additional property buckets that can be sent with the fired event (optional)
 */

// WA supported properties for modal events
// These properties match the allowed schema. Use these properties and property buckets vs adding specific custom properties
// Custom properties should not be used unless something is needed beyond these default properties and property buckets
// Do not override properties that are set in the event definition.
export const modalEventsProps = {
  name: 'set by definition - do not override',
  parentPageName: 'Required. Passed by caller when triggering the event'
};

export const modalEvents = defineModalEvents(
  modalEventsProps,
  `
  # ----------------------------------------------------------------
  # --------------------- New IA Only ------------------------------
  # ----------------------------------------------------------------
  # --------------------- Actions-----------------------------------
  templatePreview:          "Template Actions Preview Modal"      {common assistant environment skill action}

  # ----------------------------------------------------------------
  # --------------------- New IA and Classic -----------------------
  # ----------------------------------------------------------------
  # --------------------- Actions-----------------------------------
   unsavedEdits:             "Unsaved Edits"                      {common assistant environment skill}
   globalVariable:           "Global Variable"                    {common assistant environment skill}

   # --------------------- Other -----------------------------------
   trialExtend:              "Trial Extend Modal Viewed"          {common}
   trialExport:              "Trial Export Skills Modal Viewed"   {common}
   trialUpgrade:             "Trial Upgrade Modal Viewed"         {common}
   trialReminder:            "Trial Reminder Popup"               {common}
   `
);

/*
 * Experiment events:
 *   Tracks Tooling experiments within Watson Assistant
 *
 * Event definition:
 *     triggerName         - label used to trigger the event from the code. Part of definition. Not used in Amplitude
 *     name                - Typically a brief description of the experiment. Used to set the 'name' property in Segment/Amplitude. Part of definition. Do not override
 *     {property buckets}  - additional property buckets that can be sent with the fired event (optional)
 */

// Supported and default properties for experiment events.
// These properties match the allowed schema. Use these properties and property buckets vs adding specific custom properties
// Custom properties should not be used unless something is needed beyond these default properties and property buckets
// Do not override properties that are set in the event definition.
export const experimentEventProps = {
  name: 'set by definition - do not override', // eg 'Assistant Deleted',
  experimentId: 'Required. Passed by caller when triggering the event', // Unique experiment id
  experimentName: 'Required. Passed by caller when triggering the event', // Name describing the experiment,
  variationId: null, //Not required. Passed by caller when triggering the event. ID representing the variation of the experiment (if there is a flag/id representation)
  variationName: null, //Not required. Passed by caller when triggering the event. Name describing the variation of the experiment (eg control, experimental)
  audienceId: null, //Not required. Passed by caller when triggering the event. ID representing the audience for the experiment ((if there is a flag/id representation))
  audienceName: null, //Not required. Passed by caller when triggering the event. Name describing the audience for the experiment (eg: new accounts)
  parentPageName: null // Not Required. Passed by caller when triggering the event. Provides page where event is fired from.
};

export const experimentEvents = defineExperimentEvents(
  experimentEventProps,
  `
  viewed:             "Experiment"                                 {common assistant environment}
`
);

/*
 *  DEPRECATED DO NOT USE THIS EVENT
 *
 * Video playback event sets:
 *   Tracks view playback events. Currently this is only enabled in the classic experience
 *
 * Event definition:
 *     triggerName         - label used to trigger the event from the code. Part of definition. Not used in Amplitude
 *     action              - Typically a brief description of the video event. Used to set the 'action' property in Segment/Amplitude. Part of definition. Do not override
 *     {property buckets}  - additional property buckets that can be sent with the fired event (optional)
 */

// Supported and default properties for experiment events.
// These properties match the allowed schema. Use these properties and property buckets vs adding specific custom properties
// Custom properties should not be used unless something is needed beyond these default properties and property buckets
// Do not override properties that are set in the event definition.
export const videoEventsProps = {
  action: 'set by definition - do not override',
  videoTitle: 'Required. Passed by caller when triggering the event', //Name of the video
  url: 'Required. Passed by caller when triggering the event', // URL for the video
  totalLength: null, //Not required. Passed by caller when triggering the event.
  quality: null, //Not required. Passed by caller when triggering the event.
  videoPlayer: null //Not required. Passed by caller when triggering the event.
};

/* DEPRECATED DO NOT USE THIS EVENT
 */
export const videoEvents = defineVideoEvents(
  videoEventsProps,
  `
  videoPlaybackStarted:       "video started"         {common}
  videoPlaybackCompleted:     "video completed"       {common}
`
);

/*
 *  DEPRECATED DO NOT USE THIS EVENT
 *
 * Searched Object Events:
 *   - Any object that is 'searched' should trigger a searched Object event.
 *
 * Event definition:
 *
 *     triggerName         - label used to trigger the event from the code. Part of definition. Not used in Amplitude
 *     action              - Typically a brief description of what is being searched. Used to set the 'action' property in Segment/Amplitude. Part of definition. Do not override
 *     objectType          - Type of object that is being searched. Used to set objectType property in Segment/Ampplitude. Part of definition. Do not override
 *     {property buckets}  - additional property buckets that can be sent with the fired event (optional)
 *
 */

// WA supported properties for Searched Object events
// These properties match the allowed schema. Use these properties and property buckets vs adding specific custom properties
// Custom properties should not be used unless something is needed beyond these default properties and property buckets
// Do not override properties that are set in the event definition.
export const searchEventProps = {
  actionType: 'set by utility - do not override', // eg 'Deleted'
  action: 'set by definition - do not override', // eg 'Assistant Deleted'
  objectType: 'set by definition - do not override', // eg 'assistant'
  object: 'Required. Passed by caller when triggering the event', // Unique identifier for the object being deleted.
  query: 'Required, Passed by the caller when triggering the event', // search query
  resultValue: 'Required. Passed by caller when triggering the event', // Return message on the object delete. eg Success
  successFlag: true, // Required. Passed by the caller when triggering the event. Indicates if the delete object successful?
  parentPageName: null, // Not Required. Passed by caller when triggering the event. Provides page where event is fired from.
  category: null //Not Required. Passed by caller when triggering the event. Provides further detauls on the page that is being updated
  //process: null -> deletedObject event in IBM cloud does not support process like it does for other object events.
};

/* DEPRECATED DO NOT USE THIS EVENT
 */
export const searchedObjectsEvents = defineSearchedEvents(
  searchEventProps,
  `
  # ----------------------------------------------------------------
  # --------------------- New IA and Classic -----------------------
  # ----------------------------------------------------------------
  dialog:       "Dialog Search"               dialog               {common assistant environment skill}
  analytics:    "Analytics Search"            overview_logs        {common assistant environment skill}
`
);
